import { Link } from "react-router-dom";
import { ArrowRight, Binoculars, Crown, Heart, FlaskConical, Wrench } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { LightboxImage } from "@/components/Lightbox";
import CompanionQuiz from "@/components/CompanionQuiz";

const BEEKEEPER_IMG = "https://images.unsplash.com/photo-1586779161164-d89795b07b71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxiZWVrZWVwZXIlMjBhcGlhcnl8ZW58MHx8fHwxNzg2OTE1NDkwfDA&ixlib=rb-4.1.0&q=85";
const COMB_IMG = "https://images.unsplash.com/photo-1626285094816-39f688104ce0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxob25leWNvbWIlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NjkxNTQ5MHww&ixlib=rb-4.1.0&q=85";
const HONEY_IMG = "https://images.unsplash.com/photo-1573697610008-4c72b4e9508f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxnb2xkJTIwaG9uZXklMjBwb3VyfGVufDB8fHx8MTc4NjkxNTQ5OHww&ixlib=rb-4.1.0&q=85";

const chapters = [
  {
    num: "01",
    title: "Hive Tracking",
    tag: "Know every colony",
    copy: "Each hive gets its own living profile — location, origin, queen lineage, equipment, and season-by-season history. Whether you keep two hives or two hundred, the state of your apiary is always one glance away.",
    points: ["Per-hive profiles & yard mapping", "Queen lineage and requeening history", "Season-over-season comparisons"],
    img: COMB_IMG,
    alt: "Honeycomb texture",
  },
  {
    num: "02",
    title: "Inspection Logs",
    tag: "Seconds, not notebooks",
    copy: "A structured inspection flow built for gloved hands and sunny days. Brood pattern, queen sightings, stores, temperament, varroa counts — logged in seconds, searchable forever.",
    points: ["Guided, structured inspection flow", "Varroa counts & treatment records", "Full searchable history per hive"],
    img: BEEKEEPER_IMG,
    alt: "Beekeeper inspecting a frame",
    flip: true,
  },
  {
    num: "03",
    title: "AI-Powered Insights",
    tag: "Your records, read back",
    copy: "ApiaryKeeper's insight engine studies your logs across hives and seasons, surfacing the patterns a busy beekeeper can't — declining trends, swarm risk windows, and the right moment to act.",
    points: ["Trend & anomaly detection across hives", "Seasonal timing recommendations", "Plain-language summaries of your apiary"],
  },
  {
    num: "04",
    title: "BeeCredits™",
    tag: "Good beekeeping, rewarded",
    copy: "Consistent records make better beekeepers — and better data makes a better product. Earn BeeCredits™ for keeping your logs current and redeem them for premium features and more.",
    points: ["Earn credits for consistent record-keeping", "Redeem across the ApiaryKeeper ecosystem", "Founding-member bonus at launch"],
    img: HONEY_IMG,
    alt: "Honey dipper on golden comb",
  },
  {
    num: "05",
    title: "Ask a Bee™",
    tag: "Answers in the apiary",
    copy: "Strange comb? Missing queen? Ask a Bee™ connects your question — with the context of your own hives — to seasoned guidance, right when you're standing in front of the box.",
    points: ["Context-aware Q&A about your hives", "Built for use in the field", "Backed by your own inspection history"],
    img: "/assets/brand/asset_4.png",
    alt: "Choose your bee companion in the Ask a Bee app — Scout, Queenie, Honey, Dr. Buzz, and Keeper",
    natural: true,
    flip: true,
  },
];

const companions = [
  { icon: Binoculars, name: "Scout", role: "Friendly Beginner", copy: "Patient and encouraging — perfect for your very first season." },
  { icon: Crown, name: "Queenie", role: "Experienced Mentor", copy: "Wise and professional, focused on strong, healthy colonies." },
  { icon: Heart, name: "Honey", role: "Positive & Cheerful", copy: "Upbeat and motivating — celebrates every win with you." },
  { icon: FlaskConical, name: "Dr. Buzz", role: "Scientific Expert", copy: "Research-based and detailed — loves data and precision." },
  { icon: Wrench, name: "Keeper", role: "Practical Pro", copy: "Hands-on and solution-focused, season after season." },
];

export default function Features() {
  return (
    <>
      <section className="bg-forest-deep text-bone" data-testid="features-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-20">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-honey mb-8">Features</p>
          </FadeUp>
          <h1 className="font-display font-medium tracking-tighter leading-[0.95] text-[clamp(2.8rem,7vw,5.5rem)]" data-testid="features-heading">
            <MaskedLines
              lines={[
                <span key="a">Everything the hive</span>,
                <span key="b"><em className="text-honey not-italic font-semibold">remembers,</em> so you</span>,
                <span key="c">don't have to.</span>,
              ]}
              delay={0.2}
            />
          </h1>
          <FadeUp delay={0.8} className="mt-8 max-w-xl">
            <p className="text-bone/60 leading-relaxed text-base md:text-lg">
              Five tools, one calm home for your apiary. Built for first-year
              beekeepers and thirty-year veterans alike.
            </p>
          </FadeUp>
        </div>
      </section>

      <EditorialMarquee />

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 flex flex-col gap-24 md:gap-36" data-testid="features-chapters">
        {chapters.map((ch) => (
          <div
            key={ch.num}
            data-testid={`feature-chapter-${ch.num}`}
            className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
          >
            <div className={`lg:col-span-6 ${ch.flip ? "lg:order-2" : ""}`}>
              <FadeUp>
                <span className="font-display text-[clamp(5rem,12vw,10rem)] leading-none text-honey/40 block -mb-6 md:-mb-10 select-none">
                  {ch.num}
                </span>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest relative">
                  {ch.title}
                </h2>
                <p className="text-xs uppercase tracking-[0.25em] text-honey-dark mt-4 mb-6">{ch.tag}</p>
                <p className="text-ink/60 leading-relaxed max-w-lg mb-8">{ch.copy}</p>
                <ul className="flex flex-col gap-3">
                  {ch.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm text-forest/80">
                      <span className="w-1.5 h-1.5 rotate-45 bg-honey-dark shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </FadeUp>
            </div>
            <div className={`lg:col-span-6 ${ch.flip ? "lg:order-1" : ""}`}>
              <FadeUp delay={0.15} y={40}>
                {ch.img ? (
                  <div className="relative">
                    <div className="absolute -inset-3 border border-forest/15 rounded-[2rem] pointer-events-none" />
                    <LightboxImage
                      src={ch.img}
                      alt={ch.alt}
                      testid={`feature-chapter-img-${ch.num}`}
                      className={ch.natural
                        ? "rounded-[1.75rem] w-full max-w-sm mx-auto h-auto"
                        : "rounded-[1.75rem] w-full h-[22rem] md:h-[28rem] object-cover"}
                    />
                  </div>
                ) : (
                  <div className="bg-forest rounded-[1.75rem] h-[22rem] md:h-[28rem] flex items-center justify-center p-10 relative overflow-hidden">
                    <span className="font-display italic text-3xl md:text-4xl text-bone/90 text-center leading-snug">
                      "The swarm you catch<br />is the one you <span className="text-honey">saw coming.</span>"
                    </span>
                    <span className="absolute -bottom-8 -right-4 font-display text-[9rem] leading-none text-honey/15 select-none">
                      {ch.num}
                    </span>
                  </div>
                )}
              </FadeUp>
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="companions-section">
        <FadeUp className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-5">The whole toolkit</p>
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest">
            One app. Every tool you need.
          </h2>
          <p className="text-ink/60 max-w-xl mx-auto mt-4">
            Hive to harvest, yard map to CPA report — the full ApiaryKeeper toolkit,
            up close. Tap to view full screen.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <LightboxImage
            src="/assets/brand/asset_goto.png"
            alt="ApiaryKeeper — the go-to app for beekeepers: dashboard, bee yard map, inspections, reports, and plans"
            testid="toolkit-image"
            className="w-full h-auto rounded-[2rem] border border-forest/10"
          />
        </FadeUp>

        <FadeUp className="text-center mt-20 mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-5">Ask a Bee™</p>
          <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tighter text-forest">
            …with five bee companions answering your questions.
          </h3>
        </FadeUp>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12" data-testid="companion-bios">
          {companions.map((c, i) => (
            <FadeUp key={c.name} delay={0.1 + i * 0.06}>
              <div
                className="h-full bg-white border border-forest/10 rounded-3xl p-6 text-center transition-colors duration-500 hover:border-honey"
                data-testid={`companion-card-${c.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-honey/15 mb-4">
                  <c.icon className="w-6 h-6 text-honey-dark" strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-xl text-forest">{c.name}</h3>
                <p className="text-[10px] uppercase tracking-[0.2em] text-honey-dark mt-1 mb-3">{c.role}</p>
                <p className="text-xs text-ink/60 leading-relaxed">{c.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-12">
          <CompanionQuiz />
        </FadeUp>
      </section>

      <section className="bg-forest text-bone" data-testid="features-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tighter leading-tight">
              Ready when the bees are.
            </h2>
            <p className="text-bone/60 mt-3 max-w-md">Join the launch list and be first through the door.</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/pre-register?src=features"
              data-testid="features-cta-button"
              className="group inline-flex items-center gap-2 bg-honey text-forest font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-honey-light"
            >
              Join the Launch List
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
